// package imports
import { useState } from 'react';
import { BaseRecord, useTranslate } from '@refinedev/core';
import { Button, Space, Table } from 'antd';
import moment from 'moment';
// project imports
import constants from 'core/constants/Constants';
import {
  CloseOutlined,
  DownloadOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import directFileDownload from 'core/utils/directFileDownload';
import apiEndpoint from 'core/common/apiEndPoints';
import resourceName from 'core/common/resourceName';
import { DeleteButton, ShowButton } from '@refinedev/antd';

import openNotificationWithIcon from 'core/utils/notification';
import replacePipe from 'core/utils/replacePipe';
import dayjs from 'dayjs';
import { Images } from 'core/common/imagePath';
import getSocketEventStatus from 'core/utils/getSocketEventStatus';
import ErrorStackModal from 'core/utils/errorStackModal';
import SocketNotificationStatus from 'core/constants/SocketNotificationStatus';
import { useNavigate } from 'react-router-dom';
// import { Fragment } from 'react/jsx-runtime';

const UnificationHistoryTable = ({
  tableProps,
  unificationHistoryTableData,
  tableQueryResult,
}) => {
  const t = useTranslate();
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalErrorData, setModalErrorData] = useState();
  const showErrorModal = (record) => {
    setModalErrorData(record);
    setIsModalOpen(true);
  };
  const isDeveloperMode = (tableProps.dataSource as BaseRecord)?.is_dev_mode;
  const createRenderDate = (position) => (_, data) => {
    const dates = data?.date?.split(' - ');
    const date = moment(dates?.[position]);
    return date.format(constants.dateFormat);
  };
  const renderStartDate = createRenderDate(0);
  const renderEndDate = createRenderDate(1);

  function formatStatus(status) {
    return status
      ?.split('-')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  function renderShowButtonUrl(row, id) {
    const dates = row?.date?.split(' - ');
    const startDate = moment(dates?.[0]).format('YYYY-MM-DD');
    const endDate = moment(dates?.[1]).format('YYYY-MM-DD');
    const columnDate = { startDate, endDate };

    return (
      <ShowButton
        hideText
        resource={resourceName.unificationHistory}
        meta={columnDate}
        recordItemId={id}
        className="a8-edit-button"
        onClick={(e) => {
          e.preventDefault();
          navigate(
            `/${resourceName.unificationHistory}/show/${id}?startDate=${startDate}&endDate=${endDate}&unificationRunMasterId=${row.unique_id}&a8BatchId=${row?.a8_batch_id}&isLiveCalculation=${row?.is_live_calculation}`
          );
        }}
      />
    );
  }

  function renderDeleteButton(record) {
    return (
      <DeleteButton
        className="unified-cancel-status a8-delete-button"
        hideText
        confirmOkText="Yes"
        recordItemId={record?.unique_id}
        icon={<CloseOutlined className="text-red-600 text-[20px]" />}
        resource={apiEndpoint.unificationRunMaster}
        dataProviderName="payoutProvider"
        successNotification={false}
        onSuccess={(data: any) => {
          const message =
            data?.data?.message || t(`notifications.deleteSuccess`);
          openNotificationWithIcon(
            constants.success,
            t('notifications.mutationSuccess'),
            message
          );
        }}
        errorNotification={(data: any) => {
          const message =
            replacePipe(data?.message) || t('notifications.deleteError');
          return {
            message: 'Error Message',
            description: message,
            type: 'error',
          };
        }}
      />
    );
  }

  return (
    <>
      <Table
        {...tableProps}
        bordered
        dataSource={unificationHistoryTableData}
        rowKey="id"
        // scroll={{ x: 767 }}
        rowClassName={(record: any) =>
          (record as any).status === SocketNotificationStatus?.failed
            ? 'table-row-error'
            : ''
        }
        loading={tableQueryResult?.isFetching}
      >
        <Table.Column
          dataIndex="frequency_name"
          className="capitalize"
          title={t('payoutHistory.payoutTableHeaders.frequency')}
        />
        <Table.Column
          dataIndex="workflow_name"
          title={t('payoutHistory.payoutTableHeaders.workflow')}
        />
        <Table.Column
          dataIndex="unified_rule_plan_mapping"
          title={t('payoutHistory.payoutTableHeaders.unifiedSourceName')}
          render={(
            _,
            row: {
              unified_rule_plan_mapping: Array<{
                unified_source_master_id: string;
                unified_source_master_name: string;
              }>;
              status?: string;
              is_payout_calculation_done?: boolean;
              a8_batch_id?: string;
              is_live_calculation?: boolean;
              unique_id?: string;
            }
          ) => (
            <table style={{ borderCollapse: 'collapse' }}>
              {row.unified_rule_plan_mapping?.map((item, index) => (
                <tr
                  key={item.unified_source_master_id}
                  className={`border-solid border-gray ${index !== 0 ? 'border-t' : 'border-t-0'} border-x-0 border-b-0`}
                >
                  <td>
                    <span style={{ display: 'block' }}>
                      {item?.unified_source_master_name}
                    </span>
                  </td>
                  <td>
                    {row?.status?.toLowerCase() === 'success' ? (
                      <Button
                        icon={<DownloadOutlined />}
                        onClick={() =>
                          directFileDownload(
                            `${apiEndpoint.downloadUnifiedFile}/?unification_run_master_id=${row?.unique_id}&unified_source_master_id=${item.unified_source_master_id}`
                          )
                        }
                      />
                    ) : null}
                    {row?.status?.toLowerCase() === 'in-progress' ||
                    (row?.status?.toLowerCase() !== 'success' &&
                      row?.status?.toLowerCase() !== 'cancelled' &&
                      row?.status?.toLowerCase() !== 'no-data' &&
                      row?.status?.toLowerCase() !== 'failed') ? (
                      <img
                        width={50}
                        height={50}
                        src={Images.LoadingGif}
                        alt="Loading..."
                      />
                    ) : null}
                  </td>
                  {row?.status?.toLowerCase() === 'success' ? (
                    <td align="right">
                      {renderShowButtonUrl(row, item.unified_source_master_id)}
                    </td>
                  ) : null}

                  {/* </div> */}
                </tr>
              ))}
            </table>
          )}
          width={250}
        />
        <Table.Column
          dataIndex="start_date"
          title={t('payoutHistory.payoutTableHeaders.startDate')}
          render={renderStartDate}
        />
        <Table.Column
          dataIndex="end_date"
          title={t('payoutHistory.payoutTableHeaders.endDate')}
          render={renderEndDate}
        />
        <Table.Column
          dataIndex="unification_started_at"
          title={t('payoutHistory.payoutTableHeaders.startedAt')}
          render={(_, data: any) =>
            data.unification_started_at
              ? dayjs(data.unification_started_at).format(
                  constants.dateAndTimeFormat
                )
              : '-'
          }
        />
        <Table.Column
          dataIndex="unification_ended_at"
          title={t('payoutHistory.payoutTableHeaders.endedAt')}
          render={(_, data: any) =>
            data.unification_ended_at
              ? dayjs(data.unification_ended_at).format(
                  constants.dateAndTimeFormat
                )
              : '-'
          }
        />
        <Table.Column
          dataIndex="updated_by_user"
          title={t('common.startedBy')}
        />
        <Table.Column
          dataIndex="status"
          title={t('payoutHistory.payoutTableHeaders.status')}
          render={(status) => formatStatus(status)}
        />

        <Table.Column
          dataIndex="action"
          title={t('table.actions')}
          render={(_, row: any) => {
            const { isFailed, isLoading } = getSocketEventStatus(row);
            const status = row?.status?.toLowerCase();
            return (
              <Space size="large">
                {isLoading ||
                (status !== 'success' &&
                  status !== 'cancelled' &&
                  status !== 'no-data' &&
                  status !== 'failed') ? (
                  <img
                    width={50}
                    height={50}
                    src={Images.LoadingGif}
                    alt="Loading..."
                  />
                ) : (
                  <>
                    {(status === 'success' || status === 'failed') &&
                    !row?.is_payout_calculation_done
                      ? renderDeleteButton(row)
                      : null}
                    {isFailed ? (
                      <Button
                        className="a8-failed-button"
                        onClick={() => showErrorModal(row)}
                        icon={
                          <ExclamationCircleOutlined className="text-red-500" />
                        }
                      />
                    ) : null}
                  </>
                )}
              </Space>
            );
          }}
          width={50}
        />
      </Table>
      <ErrorStackModal
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
        setModalErrorData={setModalErrorData}
        isDeveloperMode={isDeveloperMode}
        modalErrorData={modalErrorData}
        sourceType=""
      />
    </>
  );
};
export default UnificationHistoryTable;
